/**
 * Sistema de caché para llamadas API
 * Implementa caché en memoria con TTL (Time To Live) configurable
 */

class ApiCache {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutos por defecto
  }

  /**
   * Genera una clave única para el caché basada en la URL y parámetros
   * @param {string} url - URL de la API
   * @param {string} params - Parámetros de la query string
   * @returns {string} - Clave única para el caché
   */
  generateKey(url, params = '') {
    return `${url}?${params}`;
  }

  /**
   * Obtiene un valor del caché si existe y no ha expirado
   * @param {string} key - Clave del caché
   * @returns {any|null} - Valor cacheado o null si no existe o expiró
   */
  get(key) {
    const cached = this.cache.get(key);
    
    if (!cached) {
      return null;
    }

    // Verificar si el caché ha expirado
    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return cached.value;
  }

  /**
   * Almacena un valor en el caché con TTL específico
   * @param {string} key - Clave del caché
   * @param {any} value - Valor a cachear
   * @param {number} ttl - Tiempo de vida en milisegundos
   */
  set(key, value, ttl = null) {
    const expiresAt = Date.now() + (ttl || this.defaultTTL);
    this.cache.set(key, {
      value,
      expiresAt,
      createdAt: Date.now()
    });
  }

  /**
   * Verifica si existe una clave en el caché y no ha expirado
   * @param {string} key - Clave del caché
   * @returns {boolean} - true si existe y no ha expirado
   */
  has(key) {
    const cached = this.cache.get(key);
    
    if (!cached) {
      return false;
    }

    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Elimina una clave específica del caché
   * @param {string} key - Clave del caché (puede ser parcial para invalidar múltiples)
   */
  invalidate(key) {
    if (key.includes('*')) {
      // Invalidación por patrón
      const pattern = key.replace(/\*/g, '.*');
      const regex = new RegExp(pattern);
      
      for (const cacheKey of this.cache.keys()) {
        if (regex.test(cacheKey)) {
          this.cache.delete(cacheKey);
        }
      }
    } else {
      this.cache.delete(key);
    }
  }

  /**
   * Limpia todo el caché
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Obtiene el tamaño actual del caché
   * @returns {number} - Número de entradas en el caché
   */
  size() {
    return this.cache.size;
  }

  /**
   * Limpia entradas expiradas del caché
   */
  cleanExpired() {
    const now = Date.now();
    for (const [key, cached] of this.cache.entries()) {
      if (now > cached.expiresAt) {
        this.cache.delete(key);
      }
    }
  }
}

// Instancia singleton del caché
const apiCache = new ApiCache();

// Limpiar entradas expiradas cada 5 minutos
setInterval(() => {
  apiCache.cleanExpired();
}, 5 * 60 * 1000);

/**
 * Wrapper para funciones API que agrega caché automático
 * @param {Function} apiFunction - Función API original
 * @param {number} ttl - Tiempo de vida en milisegundos
 * @param {Function} keyGenerator - Función opcional para generar clave personalizada
 * @returns {Function} - Función envuelta con caché
 */
export function withCache(apiFunction, ttl = null, keyGenerator = null) {
  return async function(...args) {
    // Generar clave del caché
    const key = keyGenerator 
      ? keyGenerator(...args)
      : apiCache.generateKey(apiFunction.name, JSON.stringify(args));

    // Verificar si existe en caché (a menos que se fuerce refresh)
    const forceRefresh = args[args.length - 1] === true || 
                        (args[args.length - 1] && args[args.length - 1].forceRefresh === true);
    
    if (!forceRefresh) {
      const cached = apiCache.get(key);
      if (cached !== null) {
        return cached;
      }
    }

    // Llamar a la función API original
    try {
      const result = await apiFunction(...args);
      
      // Solo cachear si el resultado es exitoso
      if (result !== false && result !== null) {
        apiCache.set(key, result, ttl);
      }
      
      return result;
    } catch (error) {
      throw error;
    }
  };
}

/**
 * Constantes de TTL predefinidas
 */
export const TTL = {
  SHORT: 5 * 60 * 1000,      // 5 minutos
  MEDIUM: 30 * 60 * 1000,     // 30 minutos
  LONG: 60 * 60 * 1000,       // 1 hora
  VERY_LONG: 24 * 60 * 60 * 1000 // 24 horas
};

export default apiCache;

